/**
 * Дата «ДД.ММ.ГГГГ» из ISO-даты сервера («2026-09-15»).
 *
 * Разбирается строкой, а не через Date: new Date("2026-09-15") — полночь
 * UTC, и западнее Гринвича она показалась бы предыдущим днём.
 * Нераспознанное значение возвращается как есть — лучше показать
 * исходную строку, чем пустое место.
 */
export function formatDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return iso;
  const [, year, month, day] = match;
  return `${day ?? ""}.${month ?? ""}.${year ?? ""}`;
}

/** Часовой пояс системы: сервер считает дни и подписывает время по Москве. */
export const SYSTEM_TIME_ZONE = "Europe/Moscow";

// en-CA форматирует дату как «ГГГГ-ММ-ДД» — ровно формат поля type="date".
const isoDay = new Intl.DateTimeFormat("en-CA", {
  timeZone: SYSTEM_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Сегодняшняя дата «ГГГГ-ММ-ДД» по Москве — как её считает сервер.
 * По часам браузера в другом поясе «сегодня» расходилось бы с проверкой
 * срока на сервере; toISOString дал бы дату UTC.
 */
export function todayIso(now: Date = new Date()): string {
  return isoDay.format(now);
}

const clockParts = new Intl.DateTimeFormat("en-GB", {
  timeZone: SYSTEM_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/**
 * Миллисекунды до ближайшей полуночи по Москве — когда сервер начинает
 * новый день. Полночь по часам браузера в другом поясе не совпала бы
 * с моментом, когда гаснут дневные отметки.
 */
export function msUntilSystemMidnight(now: Date = new Date()): number {
  const part = (type: string) =>
    Number(clockParts.formatToParts(now).find((item) => item.type === type)?.value ?? 0);
  const elapsed =
    ((part("hour") * 60 + part("minute")) * 60 + part("second")) * 1000 + now.getMilliseconds();
  return 24 * 60 * 60 * 1000 - elapsed;
}

const pad = (value: number) => String(value).padStart(2, "0");

/** Прошлый календарный месяц от даты «ГГГГ-ММ-ДД»: первое и последнее число. */
export function previousMonth(today: string): { from: string; to: string } {
  const [year, month] = today.split("-").map(Number) as [number, number];
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonth = month === 1 ? 12 : month - 1;
  // День 0 следующего месяца — последний день нужного.
  const lastDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  return {
    from: `${String(prevYear)}-${pad(prevMonth)}-01`,
    to: `${String(prevYear)}-${pad(prevMonth)}-${pad(lastDay)}`,
  };
}
